import {NyButton, NyForm, NyModal} from 'nyse-web-tools-common/lib/react';
import NySelectInput from 'nyse-web-tools-common/lib/react/form/NySelectInput/NySelectInput';
import NySubmitBtn from 'nyse-web-tools-common/lib/react/form/NySubmitBtn/NySubmitBtn';
import connect from 'nyse-web-tools-common/lib/utils/redux.connect';
import {FieldArray, useFormikContext} from 'formik';
import PropTypes from 'prop-types';
import React, {useEffect, useRef, useState} from 'react';

import {MmIdSelect} from '../../selects/mmid.select';
import {SymbolTypeaheadInput} from '../../input/SymbolTypeaheadInput';
import {getMMTypes} from '../../../stores/mmTypes/mmTypes.actions';
import {createMmAppointments} from '../../../stores/mmAppointments/mmAppointments.actions';
import {
    findDuplicateAppointmentIndexes,
    summarizeBatchResult
} from '../../../stores/mmAppointments/mmAppointments.helpers';

export const MAX_ROWS = 25;

// Trim before testing completeness, not after. toAppointment trims on the way
// out, so a whitespace-only entry would otherwise pass this filter and submit an
// empty mmid or symbol that only the server rejects, which is exactly the
// request-level error this modal exists to pre-empt.
const isCompleteRow = (row) => Boolean(row.mmid?.trim()) && Boolean(row.symbol?.trim())
    && row.mmType !== '' && row.mmType != null;

const toAppointment = (row) => ({
    mmid: row.mmid.trim(),
    symbol: row.symbol.trim().toUpperCase(),
    mmType: Number(row.mmType)
});

const apptKey = (appt) => `${appt.mmid}|${appt.symbol}|${appt.mmType}`;

const FormFields = ({userCrd, mmTypeOptions, isCreating, onClose, errorMessage, errorLines}) => {
    const {values} = useFormikContext();

    return (
        <>
            <div className='modal-body' style={{minHeight: '300px', maxHeight: '70vh', overflowY: 'auto'}}>
                {errorMessage && (
                    <div className='alert alert-danger space-bottom-2'>
                        <div>{errorMessage}</div>
                        {errorLines.length > 0 && (
                            <ul className='space-top-1 space-bottom-0'>
                                {errorLines.map((line, index) => (
                                    <li key={index}>
                                        {line.label ? <strong>{line.label}: </strong> : null}{line.message}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
                <FieldArray name='rows'>
                    {({push, remove}) => (
                        <div>
                            <div className='space-bottom-2'>
                                {values.rows.map((row, index) => (
                                    <div key={index} className='row space-bottom-1'>
                                        <div className='col-md-3'>
                                            {index === 0 && <label>MMID</label>}
                                            <MmIdSelect
                                                crd={userCrd}
                                                name={`rows[${index}].mmid`}
                                                disableInactiveControl
                                                hideEtpid
                                                required
                                                menuPortalTarget={document.body}
                                                menuPosition='fixed'
                                            />
                                        </div>
                                        <div className='col-md-4'>
                                            {index === 0 && <label>Symbol</label>}
                                            <SymbolTypeaheadInput
                                                name={`rows[${index}].symbol`}
                                                maxLength={24}
                                                placeholder='Search symbol...'
                                                required
                                                limit={10}
                                            />
                                        </div>
                                        <div className='col-md-3'>
                                            {index === 0 && <label>MM Type</label>}
                                            <NySelectInput
                                                name={`rows[${index}].mmType`}
                                                options={mmTypeOptions}
                                                disabled={!mmTypeOptions.length}
                                                required
                                                showLabelAsValue
                                            />
                                        </div>
                                        <div className='col-md-2'>
                                            {index === 0 && <label>&nbsp;</label>}
                                            {index > 0 && (
                                                <NyButton
                                                    danger
                                                    small
                                                    onClick={() => remove(index)}
                                                    disabled={isCreating}>
                                                    Remove
                                                </NyButton>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className='row'>
                                <div className='col-md-12'>
                                    <button
                                        type='button'
                                        className='btn btn-info'
                                        onClick={() => push({mmid: '', symbol: '', mmType: ''})}
                                        disabled={isCreating || values.rows.length >= MAX_ROWS}>
                                        Add More
                                    </button>
                                    {values.rows.length >= MAX_ROWS && (
                                        <span className='text-muted small space-left-1'>
                                            Maximum {MAX_ROWS} rows reached
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </FieldArray>
            </div>

            <div className='modal-footer'>
                <NySubmitBtn disabled={isCreating} className='space-right-1'>
                    {isCreating ? 'Saving...' : 'Save'}
                </NySubmitBtn>
                <NyButton
                    primary
                    onClick={onClose}
                    disabled={isCreating}>
                    Cancel
                </NyButton>
            </div>
        </>
    );
};

const AddAppointmentsModalComponent = (props) => {
    const {
        userCrd,
        onClose,
        show,
        hideModal,
        mmAppointments,
        mmTypes,
        getMMTypes,
        createMmAppointments
    } = props;

    const isMountedRef = useRef(false);
    const hadPartialSuccessRef = useRef(false);
    // Keys (mmid|symbol|mmType) GBO already accepted on a prior attempt. After a
    // partial success the form keeps every row visible, so without this guard a
    // re-Save (to correct the failed rows) would re-submit the accepted ones too.
    const succeededKeysRef = useRef(new Set());
    const [errorMessage, setErrorMessage] = useState(null);
    const [errorLines, setErrorLines] = useState([]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (show && (!mmTypes.options || mmTypes.options.length === 0)) {
            getMMTypes();
        }
    }, [show, mmTypes.options, getMMTypes]);

    const isCreating = mmAppointments.isCreating;

    const initialValues = {
        rows: [{mmid: '', symbol: '', mmType: ''}]
    };

    const onSubmit = async (values) => {
        if (isMountedRef.current) {
            setErrorMessage(null);
            setErrorLines([]);
        }

        // mic is derived from the Authorization header by cdm-mw; crd travels as the
        // userCrd param (same contract as Symbol MPID Clearing) — neither is sent per row.
        const appointments = values.rows
            .filter(isCompleteRow)
            .map(toAppointment)
            // Drop any row GBO already accepted on a prior attempt so a retry never
            // re-submits a successful add. Editing a row changes its key, so a
            // corrected-and-resaved row is (correctly) treated as a new submission.
            .filter((appt) => !succeededKeysRef.current.has(apptKey(appt)));

        if (appointments.length === 0) {
            return;
        }

        // CS 11009 §11009.6.3: an Add array may not carry the same MMID + symbol twice, and a GBO
        // row is keyed (crd, mmid, symbol) with mm_type overwriteable, so a repeat is one
        // appointment written twice and the surviving type would depend on dispatch order
        // (TOP-5791). Name the offending rows here rather than surfacing one request-level
        // error the user has to map back themselves. Checked against the payload, so a row already
        // accepted on a prior attempt (filtered out above) cannot make a legitimate row look
        // duplicated.
        const duplicates = findDuplicateAppointmentIndexes(appointments);

        if (duplicates.size > 0) {
            if (isMountedRef.current) {
                setErrorMessage('The same MMID and symbol cannot be added twice in one request. '
                    + 'Remove the duplicate row and save again:');
                setErrorLines([...duplicates].map((index) => ({
                    label: `${appointments[index].mmid} / ${appointments[index].symbol}`,
                    message: 'entered more than once'
                })));
            }
            return;
        }

        try {
            const action = await createMmAppointments({userCrd, appointments});
            const {successCount, errorCount, total, errorLines: failures} =
                summarizeBatchResult(action?.payload?.data?.data, appointments.length);

            // Record the rows accepted this attempt (attempted minus failed) so a
            // subsequent Save won't resend them.
            const failedKeys = new Set(failures.map(apptKey));
            appointments
                .filter((appt) => !failedKeys.has(apptKey(appt)))
                .forEach((appt) => succeededKeysRef.current.add(apptKey(appt)));

            // POST returns 207 (a 2xx) on partial failure, so per-row errors arrive
            // here on the success path, not via catch().
            if (errorCount === 0) {
                // GBO accepts the write immediately but the Pillar sync that makes it
                // EFFECTIVE runs asynchronously, so the grid won't reflect the change on the
                // re-fetch that fires right after this toast. Set that expectation here so
                // users don't read the row's absence as a failure.
                //
                // ADD timing (CS 11009 §11009.12/§11009.20): normal-path sync is ~1 min. Adds
                // received during the opening-auction window are STAGED (backoffice only) and
                // then RELEASED downstream once the after-core-auction offset expires — i.e.
                // genuinely just "longer", unlike deletes which are held to next-day. Adds
                // received after the closing auction post for next-day. The synchronous GBO
                // response carries no zone indicator (responseData.status is null), so we
                // describe the behavior rather than promise an exact ETA.
                const successMessage =
                    `${successCount} MM appointment${successCount === 1 ? '' : 's'} submitted — `
                    + 'changes typically appear within ~1 minute. Submissions near the opening '
                    + 'auction may be staged and applied once the auction completes.';

                setTimeout(() => {
                    if (isMountedRef.current) {
                        onClose(true, successMessage);
                    }
                }, 0);
                return;
            }

            if (isMountedRef.current) {
                if (successCount > 0) {
                    hadPartialSuccessRef.current = true;
                }
                setErrorMessage(
                    successCount > 0
                        ? `${successCount} of ${total} appointment(s) added — ${errorCount} failed:`
                        : `Failed to add ${errorCount} appointment(s):`
                );
                setErrorLines(failures);
            }
        } catch (error) {
            const errorMsg = error?.error?.detail
                || error?.error?.title
                || error?.error?.message
                || error?.message
                || 'Failed to save appointments. Please try again.';

            if (isMountedRef.current) {
                setErrorMessage(errorMsg);
                setErrorLines([]);
            }
        }
    };

    // Only offer self-serviceable MM Types (CS 11009 §11009.11 whitelist). The backend tags
    // each /api/mm-types option with `eligible`; hiding the ineligible ones stops a user from
    // submitting a type GBO/CRS would reject. `!== false` leaves the list intact if the flag
    // is ever absent (older backend), degrading to the prior show-all behavior.
    // Show GBO's verbose display string (`ARCA Options MM`, …) so the Add dropdown matches the
    // grid's MM Type column; fall back to the short `label` if a backend hasn't supplied `gboLabel`.
    const eligibleMmTypeOptions = (mmTypes.options || [])
        .filter((opt) => opt.eligible !== false)
        .map((opt) => ({...opt, label: opt.gboLabel ?? opt.label}));

    return (
        <NyModal
            show={show}
            hideModal={hideModal}
            title='Add MM Appointments'
            className='modal-medium'>
            <NyForm
                initialValues={initialValues}
                onSubmit={onSubmit}>
                <FormFields
                    userCrd={userCrd}
                    mmTypeOptions={eligibleMmTypeOptions}
                    isCreating={isCreating}
                    onClose={() => onClose(hadPartialSuccessRef.current)}
                    errorMessage={errorMessage}
                    errorLines={errorLines}
                />
            </NyForm>
        </NyModal>
    );
};

AddAppointmentsModalComponent.propTypes = {
    userCrd: PropTypes.number.isRequired,
    show: PropTypes.bool.isRequired,
    hideModal: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    getMMTypes: PropTypes.func.isRequired,
    createMmAppointments: PropTypes.func.isRequired,
    mmAppointments: PropTypes.shape({
        isCreating: PropTypes.bool,
        createError: PropTypes.object
    }),
    mmTypes: PropTypes.shape({
        options: PropTypes.array
    })
};

export const AddAppointmentsModal = connect(
    ['mmAppointments', 'mmTypes'],
    {getMMTypes, createMmAppointments}
)(AddAppointmentsModalComponent);
